import React, { useState, lazy } from 'react';
export function Reason3() {
  const [temperature, setTemperature] = useState(42);
  const getTemperatureColor = (temp: number) => {
    if (temp < 38) return 'bg-blue-100';
    if (temp === 42) return 'bg-[#0EA5B7]';
    if (temp > 43) return 'bg-red-400';
    return 'bg-green-300';
  };
  const getTemperatureEffect = (temp: number) => {
    if (temp < 38) return 'No therapeutic effect';
    if (temp === 42) return 'Optimal thermoregulatory vasodilation';
    if (temp > 43) return 'Defensive constriction';
    return 'Partial therapeutic effect';
  };
  const acupressurePoints = [{
    id: 'BL1',
    x: 30,
    y: 35,
    label: 'BL1',
    desc: 'Inner eyebrow'
  }, {
    id: 'BL2',
    x: 70,
    y: 35,
    label: 'BL2',
    desc: 'Inner eyebrow'
  }, {
    id: 'GB8',
    x: 20,
    y: 50,
    label: 'GB8',
    desc: 'Temple region'
  }, {
    id: 'TB23',
    x: 80,
    y: 40,
    label: 'TB23',
    desc: 'Outer eyebrow'
  }];
  return <div className="py-12 border-t border-[#F1F5F9]" data-id="element-336">
      <div className="flex flex-col md:flex-row md:gap-8" data-id="element-337">
        <div className="md:w-2/3" data-id="element-338">
          <div className="flex items-center mb-4" data-id="element-339">
            <div className="bg-[#0EA5B7] text-white font-bold rounded-full w-10 h-10 flex items-center justify-center mr-4" data-id="element-340">
              3
            </div>
            <h2 className="text-2xl font-bold text-[#0F172A]" data-id="element-341">Reason #3:</h2>
          </div>
          <h3 className="text-xl font-semibold text-[#0F172A] mb-6" data-id="element-342">
            The 42°C Discovery That Rewrites Everything (Why This Exact
            Temperature Targets Your Pain's Command Center)
          </h3>
          <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-343">
            <div className="rounded-lg overflow-hidden shadow-md h-[300px]" data-id="element-344">
              <picture className="w-full h-full" data-id="element-345">
                <source srcSet="https://uploadthingy.s3.us-west-1.amazonaws.com/2qBgC7hyVLsfqKdvK17AoC/Gemini_Generated_Image_pzxzompzxzompzxz.webp" type="image/webp" data-id="element-346" />
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2qBgC7hyVLsfqKdvK17AoC/Gemini_Generated_Image_pzxzompzxzompzxz.webp" alt="Woman wearing iRelief Oculis-3 with 104-113°F constant temperature hot compress" className="w-full h-full object-cover" width="400" height="300" loading="lazy" data-id="element-347" />
              </picture>
            </div>
            <div className="rounded-lg overflow-hidden shadow-md h-[300px]" data-id="element-348">
              <picture className="w-full h-full" data-id="element-349">
                <source srcSet="https://uploadthingy.s3.us-west-1.amazonaws.com/idkeZsQPqjeCWGnh9zqm7i/imgi_85_711Xha8JqbL._AC_SL1500_ab464591-5e30-4280-9f98-76cdeacd02ac.webp" type="image/webp" data-id="element-350" />
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/idkeZsQPqjeCWGnh9zqm7i/imgi_85_711Xha8JqbL._AC_SL1500_ab464591-5e30-4280-9f98-76cdeacd02ac.webp" alt="Eye massager combining kneading and compression massage techniques" className="w-full h-full object-cover" width="400" height="300" loading="lazy" data-id="element-351" />
              </picture>
            </div>
          </div>
          <div className="prose prose-slate max-w-none" data-id="element-352">
            <p data-id="element-353">107.6°F.°</p>
            <p data-id="element-354">That's 42°C for those keeping track.</p>
            <p data-id="element-355">It's also the exact temperature that changes everything.</p>
            <p data-id="element-356">
              See, I always thought heat was heat. Ice was ice. Pressure was
              pressure. Apply them randomly, hope something helps.
            </p>
            <p data-id="element-357">Wrong.</p>
            <p data-id="element-358">
              A Taiwan medical center study found that 42°C is the precise
              temperature that triggers something called "thermoregulatory
              vasodilation" — your blood vessels open in a specific pattern that
              flushes out inflammatory compounds while preserving healthy
              circulation.
            </p>
            <p data-id="element-359">
              Too hot? You trigger defensive constriction. Too cool? No
              therapeutic effect.
            </p>
            <p data-id="element-360">But at around 38-43°C, something remarkable happens...</p>
            <p data-id="element-361">
              The iRelief Oculis-3 maintains this temperature while
              simultaneously targeting four specific acupressure points that
              traditional Chinese medicine has used for 2,000 years:
            </p>
            <ul className="list-disc pl-6 space-y-2 my-4" data-id="element-362">
              <li data-id="element-363">
                <strong data-id="element-364">BL1 and BL2</strong> (inner eyebrow): Direct connection
                to the ophthalmic branch of your trigeminal nerve
              </li>
              <li data-id="element-365">
                <strong data-id="element-366">GB8</strong> (temple region): Influences temporal blood
                flow and relieves that "vise-grip" sensation
              </li>
              <li data-id="element-367">
                <strong data-id="element-368">TB23</strong> (outer eyebrow): Regulates the nerve
                signals that create light sensitivity
              </li>
            </ul>
            <p data-id="element-369">
              When you combine 42°C heat with compression at these exact points,
              you're not just "relaxing muscles." You're sending a specific
              signal to your trigeminal nerve that essentially says: "Stand
              down. Threat eliminated."
            </p>
            <p data-id="element-370">
              A New Zealand randomized controlled trial proved this. Forty-seven
              chronic migraine sufferers. Six weeks of this combined therapy.
              Result? Significant reduction in migraine frequency that persisted
              three weeks after treatment ended.
            </p>
            <p data-id="element-371">
              No pills. No injections. Just physics and physiology working
              together.
            </p>
            <p data-id="element-372">
              One user put it simply:{' '}
              <em data-id="element-373">
                "I use high pressure with heat as soon as I feel a migraine
                coming on... Best purchase I've made in a while."
              </em>
            </p>
            <p data-id="element-374">
              When you add the 42°C sweet spot? That's when the magic happens.
            </p>
          </div>
        </div>
        <div className="md:w-1/3 mt-8 md:mt-0" data-id="element-375">
          <div className="bg-white rounded-lg shadow-sm border border-[#F1F5F9] p-4 mb-6" data-id="element-376">
            <h4 className="font-medium text-[#0F172A] mb-4" data-id="element-377">
              Thermal Set-Point
            </h4>
            <div className="mb-4 flex justify-between text-xs text-[#334155]" data-id="element-378">
              <span data-id="element-379">35°C</span>
              <span data-id="element-380">40°C</span>
              <span data-id="element-381">45°C</span>
            </div>
            <div className="h-4 bg-gradient-to-r from-blue-100 via-green-300 to-red-400 rounded-full mb-4 relative" data-id="element-382">
              <div className={`absolute top-0 bottom-0 w-1.5 rounded-full transform -translate-x-1/2 transition-all duration-300 ${temperature === 42 ? 'animate-pulse' : ''}`} style={{
              left: `${(temperature - 35) / 10 * 100}%`,
              backgroundColor: temperature === 42 ? '#0EA5B7' : '#0F172A'
            }} data-id="element-383" />
            </div>
            <input type="range" min="35" max="45" step="1" value={temperature} onChange={e => setTemperature(parseInt(e.target.value))} className="w-full h-2 bg-[#F1F5F9] rounded-lg appearance-none cursor-pointer mb-4" data-id="element-384" />
            <div className={`text-center p-3 rounded-md text-white font-medium ${getTemperatureColor(temperature)}`} data-id="element-385">
              {temperature}°C
            </div>
            <div className="text-sm text-center mt-2 text-[#334155]" data-id="element-386">
              {getTemperatureEffect(temperature)}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-[#F1F5F9] p-4" data-id="element-387">
            <h4 className="font-medium text-[#0F172A] mb-4" data-id="element-388">
              Clinical Evidence
            </h4>
            <div className="flex items-center space-x-4" data-id="element-389">
              <div className="w-16 h-16 rounded-full bg-[#F1F5F9] flex items-center justify-center text-[#0EA5B7] font-bold text-xl" data-id="element-390">
                47
              </div>
              <div className="flex-1" data-id="element-391">
                <div className="text-sm font-medium text-[#0F172A]" data-id="element-392">
                  Participants
                </div>
                <div className="text-xs text-[#334155]" data-id="element-393">
                  Chronic migraine sufferers
                </div>
              </div>
            </div>
            <div className="my-4 h-px bg-[#F1F5F9]" data-id="element-394"></div>
            <div className="flex items-center space-x-4" data-id="element-395">
              <div className="w-16 h-16 rounded-full bg-[#F1F5F9] flex items-center justify-center text-[#0EA5B7] font-bold text-xl" data-id="element-396">
                6
              </div>
              <div className="flex-1" data-id="element-397">
                <div className="text-sm font-medium text-[#0F172A]" data-id="element-398">Weeks</div>
                <div className="text-xs text-[#334155]" data-id="element-399">
                  Combined therapy duration
                </div>
              </div>
            </div>
            <div className="my-4 h-px bg-[#F1F5F9]" data-id="element-400"></div>
            <div className="text-center p-3 bg-[#F1F5F9] rounded-md" data-id="element-401">
              <div className="text-sm font-medium text-[#0F172A]" data-id="element-402">
                Significant reduction
              </div>
              <div className="text-xs text-[#334155]" data-id="element-403">
                in migraine frequency that persisted three weeks after treatment
                ended
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}